"use client";

import { useState } from "react";
import styles from "./index.module.scss";
import BlessButton from "@/components/layout/button/green-outline-btn";
import ServiceHeader from "@/components/layout/service-header";
import Link from "next/link";
import {
  BLOCKCHAINS_BY_NETWORK,
  type ChainData,
} from "@/constants/chain-details";
import BlessIcon from "@public/icon_color.png";
import EthIcon from "@public/chain-logos/low-res/ethereum-logo.png";
import { ethers, formatUnits } from "ethers";
import { convertAndOperate } from "@/utils/convert-eth-to-bless.util";
import { useAccount } from "wagmi";
import LoadingIcon from "@/components/layout/loading-icon";
import { motion } from "framer-motion";
import BlessThinking from "@public/web-content/bless-thinking.png";
import Image from "next/image";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import classNames from "classnames";
import useDispenser from "@/features/dispenser/hooks/useDispenser";
import ChainBtn from "@/features/dispenser/components/chain-btn";
import DispenserInput from "@/features/dispenser/components/input";
import { showToast } from "@/components/layout/toast/show-toast";
import { ChainListItem } from "@/components/layout/popup-list/list-items/chain-list-item";
import PopupList from "@/components/layout/popup-list/popup-list";

const Dispenser = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { isConnected } = useAccount();
  const { chainId } = useAccount();
  const { openConnectModal } = useConnectModal();

  const {
    allSettingsQuery,
    requestBless,
    state: {
      ethInput,
      blessInput,
      setEthInput,
      setBlessInput,
      recipientChain,
      setRecipientChain,
      isSigningPending,
    },
  } = useDispenser();

  //* common code for setting BLESS value
  const setBlessValue = (blessValue: string) => {
    setBlessInput(blessValue);
    const eth = convertAndOperate(
      blessValue,
      allSettingsQuery.data!.rate,
      "divide",
    );
    setEthInput(eth.toString());
  };

  //* common code for setting ETH value
  const setETHValue = (ethValue: string) => {
    setEthInput(ethValue);
    const bless = convertAndOperate(
      ethValue,
      allSettingsQuery.data!.rate,
      "multiply",
    );
    setBlessInput(bless.toString());
  };

  //* handles min input value
  const handleMin = () => {
    if (!allSettingsQuery.data) return;
    const min = formatUnits(allSettingsQuery.data!.minimumBless);
    setBlessValue(min);
  };

  //* handles max input value
  const handleMax = () => {
    if (!allSettingsQuery.data) return;
    const max = formatUnits(allSettingsQuery.data!.maximumBless);
    setBlessValue(max);
  };

  //* reset inputs
  const resetInputs = (initValue?: string) => {
    setEthInput(initValue ?? "");
    setBlessInput(initValue ?? "");
  };

  //* check if above the minimum bless allowed
  const isBelowBlessMinimum = (value: string | undefined) => {
    if (!value) return false;
    const parsed = ethers.parseEther(value);
    const units = allSettingsQuery.data!.minimumBless;
    return parsed < units;
  };

  //* check if above the maximum bless allowed
  const isAboveBlessMaximum = (value: string | undefined) => {
    if (!value) return false;
    const parsed = ethers.parseEther(value);
    const units = allSettingsQuery.data!.maximumBless;
    console.log(parsed, units);
    return parsed > units;
  };

  //* handle the input of the bless input
  const handleBlessInput = (value: string) => {
    if (!allSettingsQuery.data || !value) return;
    if (shouldInputBeEmptied(value)) return resetInputs();
    if (value === ".") return resetInputs("0.");

    setBlessValue(value);
  };

  //* handle the input of the eth input
  const handleEthInput = (value: string) => {
    if (!allSettingsQuery.data || !value) return false;
    if (shouldInputBeEmptied(value)) return resetInputs();
    if (value === ".") return resetInputs("0.");

    setETHValue(value);
  };

  //* Selects chain
  const handleSelectChain = (chain: ChainData) => {
    setRecipientChain(chain);
  };

  const handleOpenMenu = () => {
    setIsOpen(true);
  };

  const shouldInputBeEmptied = (value: string) => {
    return value === "0" || value === "";
  };

  const areInputsValid = (): boolean => {
    // when blurred check if the value is below the minimum
    if (isBelowBlessMinimum(blessInput)) {
      const min = formatUnits(allSettingsQuery.data!.minimumBless);
      setBlessValue(min);
      showToast({
        toastContent: {
          header: "Input value is below the minimum",
          message: `The amount you entered is below the minimum limit of ${formatUnits(
            allSettingsQuery.data!.minimumBless.toString(),
          )} BLESS.`,
          status: "warning",
        },
      });
      return false;
    }
    if (isAboveBlessMaximum(blessInput)) {
      const max = formatUnits(allSettingsQuery.data!.maximumBless);
      setBlessValue(max);
      showToast({
        toastContent: {
          header: "Input value is above the maximum",
          message: `The amount you entered is above the maximum limit of ${formatUnits(
            allSettingsQuery.data!.maximumBless.toString(),
          )} BLESS.`,
          status: "warning",
        },
      });
      return false;
    }

    return true;
  };

  const handleRetrieveBless = async () => {
    if (!isConnected) return openConnectModal?.();
    if (!ethInput || !blessInput || !recipientChain) {
      showToast({
        toastContent: {
          header: "Missing input values",
          message: "Please make sure you've entered all the required values.",
          status: "warning",
        },
      });
      console.error("Missing input values");
      return;
    }

    if (!areInputsValid()) return;

    const ethWei = ethers.parseEther(ethInput);
    const blessWei = ethers.parseEther(blessInput);
    const res = await requestBless(
      ethWei,
      blessWei,
      BigInt(recipientChain.chainId),
    );
    if (res) console.log(res);
  };

  return (
    <div
      className={styles.blessDispenserWrapper}
      role="region"
      aria-label="Bless Dispenser"
    >
      <div className={styles.dispenserContainer}>
        <ServiceHeader text="Bless Dispenser" />
        {/* <TopRightBtns /> */}
        <div className={styles.dispenserPanel}>
          <div className={styles.dispenserWrapper}>
            <ChainBtn
              isLoading={allSettingsQuery.isLoading}
              chain={recipientChain}
              allSettings={allSettingsQuery.data}
              onOpen={handleOpenMenu}
            />
          </div>
          <div className={styles.dispenserWrapper}>
            {/* <span className={styles.headerText}>
              Enter amount of bless
            </span> */}
            <div
              className={classNames(
                styles.spacer,
                !allSettingsQuery.data?.destinations.length && styles.disabled,
              )}
            >
              <DispenserInput
                handleMin={handleMin}
                handleMax={handleMax}
                onBlur={() => areInputsValid()}
                hideMinMax
                icon={EthIcon}
                label="Eth"
                setValue={handleEthInput}
                value={ethInput}
              />
              <DispenserInput
                handleMin={handleMin}
                onBlur={() => areInputsValid()}
                handleMax={handleMax}
                maxLength={5}
                icon={BlessIcon}
                label="Bless"
                setValue={handleBlessInput}
                value={blessInput}
              />
            </div>
          </div>
          <BlessButton
            onClick={handleRetrieveBless}
            styles={{
              width: "100%",
              height: 55,
              fontWeight: 700,
              fontSize: 16,
              zIndex: 10,
            }}
            buttonType="rainbow"
            disabled={allSettingsQuery.isFetching}
          >
            {!isConnected ? "Connect Wallet" : " GET BLESS"}
          </BlessButton>
          {!isConnected && (
            <div className={styles.notConnectedOverlay}>
              <Image
                className={styles.image}
                src={BlessThinking}
                alt="Not connected image"
                width={100}
              />
              <div className={styles.text}>
                Connect wallet to see options...
              </div>
            </div>
          )}
          {isSigningPending && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={styles.loadingOverlay}
            >
              <LoadingIcon size={40} />
              {/* <div className={styles.loader}>
                <BridgeLoader />
              </div> */}
            </motion.div>
          )}
        </div>
        <div className={styles.paragraph}>
          Get BLESS delivered to Blessnet
          <br />
          To learn more, see our <Link href="/">documentation.</Link>
        </div>
      </div>
      <PopupList<ChainData>
        listHeader="Select a chain"
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        list={BLOCKCHAINS_BY_NETWORK} // TODO Chains need to be dynamically updated based on response data
        item={(data) => <ChainListItem {...data} />}
        getId={(data) => data.chainId}
        activeId={chainId}
        activeIndex={recipientChain?.chainId}
        onItemSelect={handleSelectChain}
      />
    </div>
  );
};

export default Dispenser;
